import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import {
  CheckboxGroup,
  Datebox,
  Dropdown,
  MultiSelectDataDropdown,
  MultiSelectDropdown,
  RadioGroup,
  TextArea,
  Textbox,
} from "capitalrx-components-2.0/lib/forms";
import { DataRow, OrderableList, Panel, useRemoteTableDataSource, useSimpleTableDataSource } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive, useResponsiveDisplayMode } from "capitalrx-components-2.0/lib/layout";
import { Heading, Regular } from "capitalrx-components-2.0/lib/typography";
import { useLoosePermissionCheck } from "capitalrx-shell";

import { PricingLevelsTypes } from "@/types";

import { getApiBaseUrl } from "../../api/getApiBaseUrl";
import { DrugList, Network, ProviderAgreement } from "../../api/interfaces";
import { agreementChannel, lineOfBusiness, networkBenefits, providerAgreementType } from "../../constants";
import { useFailureHandler } from "../../hooks";
import { generateMultiSelectDropdownOptions, numberValidator } from "../../utils";
import { CompoundSection } from "./CompoundSection";
import { PricingLevelFormHierarchy } from "./Hierarchy";
import { renderLevelOptions, renderOverpaymentMethodOptions } from "./constants";

import classes from "./PricingLevelForm.module.scss";

interface Props {
  readonly defaultValues?: PricingLevelsTypes.PricingLevelBase;
}

export const PricingLevelForm: FC<Props> = (props) => {
  const { defaultValues } = props;

  const { columns612, column266, column21212 } = useResponsive({
    columns612: {
      desktop: 6,
      tablet: 12,
      mobile: 12,
    },
    column266: {
      desktop: 2,
      tablet: 6,
      mobile: 12,
    },
    column21212: {
      desktop: 2,
      tablet: 12,
      mobile: 12,
    },
  });

  const displayMode = useResponsiveDisplayMode();
  const failureHandler = useFailureHandler();
  const form = useFormContext();

  /* Dropdowns Data */
  const providerAgreementDataSourceDropdownData = useRemoteTableDataSource<ProviderAgreement.AgreementListType>(
    `${getApiBaseUrl()}/provider_agreement/list`,
    (row) => row.provider_agreement.external_provider_agreement_id,
    {
      pageSize: 15,
      idFilterName: "external_provider_agreement_ids",
      onError: failureHandler,
    },
  );

  const providerAgreementlabelProvider = useCallback((row: ProviderAgreement.AgreementListType) => {
    return `${row.provider_agreement.external_provider_agreement_id}`;
  }, []);

  const networkDataSourceDropdownData = useRemoteTableDataSource<Network.NetworkAPIResult>(
    `${getApiBaseUrl()}/network/list`,
    (row) => row.network.external_network_id,
    {
      pageSize: 15,
      idFilterName: "external_network_ids",
      onError: failureHandler,
    },
  );

  const networklabelProvider = useCallback(
    (row: Network.NetworkAPIResult) => {
      const maxLabelChars = displayMode === "mobile" ? 38 : 75;
      const label = `${row.network.external_network_id} - ${row.network.name}`;

      if (label.length <= maxLabelChars) {
        return label;
      } else {
        return `${label.substring(0, maxLabelChars - 3)}...`;
      }
    },
    [displayMode],
  );

  /* Fileds Object */
  const fieldsObj = {
    awp: (
      <Grid key="awp">
        <Grid.Span columns={column21212}>
          <Shelf alignment="center" className={classes.fullHeight}>
            <Regular>AWP</Regular>
            <Shelf.Spacer />
          </Shelf>
        </Grid.Span>

        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__awp-brand-discount"
            name="awp_brand_discount"
            label="Brand Discount"
            suffix="%"
            instructions="No commas or special characters"
          />
        </Grid.Span>

        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__awp-generic-discount"
            name="awp_generic_discount"
            label="Generic Discount"
            suffix="%"
            instructions="No commas or special characters"
          />
        </Grid.Span>

        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__awp-brand-dispensing-fee"
            name="awp_brand_dispensing_fee"
            label="Brand Dispensing Fee"
            prefix="$"
            instructions="No commas or special characters"
          />
        </Grid.Span>

        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__awp-generic-dispensing-fee"
            name="awp_generic_dispensing_fee"
            label="Generic Dispensing Fee"
            prefix="$"
            instructions="No commas or special characters"
          />
        </Grid.Span>
      </Grid>
    ),
    nadac: (
      <Grid key="nadac">
        <Grid.Span columns={column21212}>
          <Shelf alignment="center" className={classes.fullHeight}>
            <Regular>NADAC</Regular>
            <Shelf.Spacer />
          </Shelf>
        </Grid.Span>

        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__nadac-brand-discount"
            name="nadac_brand_discount"
            label="Brand Discount"
            suffix="%"
            instructions="No commas or special characters"
          />
        </Grid.Span>

        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__nadac-generic-discount"
            name="nadac_generic_discount"
            label="Generic Discount"
            suffix="%"
            instructions="No commas or special characters"
          />
        </Grid.Span>

        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__nadac-brand-dispensing-fee"
            name="nadac_brand_dispensing_fee"
            label="Brand Dispensing Fee"
            prefix="$"
            instructions="No commas or special characters"
          />
        </Grid.Span>

        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__nadac-generic-dispensing-fee"
            name="nadac_generic_dispensing_fee"
            label="Generic Dispensing Fee"
            prefix="$"
            instructions="No commas or special characters"
          />
        </Grid.Span>
      </Grid>
    ),
    specialty_list: (
      <Grid key="specialty_list">
        <Grid.Span columns={column21212}>
          <Shelf alignment="center" className={classes.fullHeight}>
            <Regular>Specialty List</Regular>
            <Shelf.Spacer />
          </Shelf>
        </Grid.Span>
        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__specialty_list-brand-discount"
            name="specialty_list_brand_discount"
            label="Brand Discount"
            suffix="%"
            instructions="No commas or special characters"
          />
        </Grid.Span>
        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__specialty_list-generic-discount"
            name="specialty_list_generic_discount"
            label="Generic Discount"
            suffix="%"
            instructions="No commas or special characters"
          />
        </Grid.Span>
        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__specialty_list-brand-dispensing-fee"
            name="specialty_list_brand_dispensing_fee"
            label="Brand Dispensing Fee"
            prefix="$"
            instructions="No commas or special characters"
          />
        </Grid.Span>
        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__specialty_list-generic-dispensing-fee"
            name="specialty_list_generic_dispensing_fee"
            label="Generic Dispensing Fee"
            prefix="$"
            instructions="No commas or special characters"
          />
        </Grid.Span>
        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__specialty-list-brand-delta-rate"
            name="specialty_list_brand_delta_rate"
            label="Brand Delta Rate"
            suffix="%"
            instructions="No commas or special characters"
          />
        </Grid.Span>
        <Grid.Span columns={column21212}>
          <div></div>
        </Grid.Span>
        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__specialty-list-generic-delta-rate"
            name="specialty_list_generic_delta_rate"
            label="Generic Delta Rate"
            suffix="%"
            instructions="No commas or special characters"
          />
        </Grid.Span>
        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__specialty-list-brand-delta-dispensing-fee"
            name="specialty_list_brand_delta_dispensing_fee"
            label="Brand Delta Dispensing Fee"
            prefix="$"
            instructions="No commas or special characters"
          />
        </Grid.Span>
        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__specialty-list-generic-delta-dispensing-fee"
            name="specialty_list_generic_delta_dispensing_fee"
            label="Generic Delta Dispensing Fee"
            prefix="$"
            instructions="No commas or special characters"
          />
        </Grid.Span>
      </Grid>
    ),

    mac_list: (
      <Grid key="mac_list">
        <Grid.Span columns={column21212}>
          <Shelf alignment="center" className={classes.fullHeight}>
            <Regular>MAC List</Regular>
            <Shelf.Spacer />
          </Shelf>
        </Grid.Span>
        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__mac_list-brand-discount"
            name="mac_list_brand_discount"
            label="Brand Discount"
            suffix="%"
            instructions="No commas or special characters"
          />
        </Grid.Span>
        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__mac_list-generic-discount"
            name="mac_list_generic_discount"
            label="Generic Discount"
            suffix="%"
            instructions="No commas or special characters"
          />
        </Grid.Span>
        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__mac_list-brand-dispensing-fee"
            name="mac_list_brand_dispensing_fee"
            label="Brand Dispensing Fee"
            prefix="$"
            instructions="No commas or special characters"
          />
        </Grid.Span>
        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__mac_list-generic-dispensing-fee"
            name="mac_list_generic_dispensing_fee"
            label="Generic Dispensing Fee"
            prefix="$"
            instructions="No commas or special characters"
          />
        </Grid.Span>
        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__mac-list-unit-price-adjustment-percentage"
            name="mac_list_unit_price_adjustment_percentage"
            label="MAC List Unit Price Adjustment Percentage"
            suffix="%"
          />
        </Grid.Span>
      </Grid>
    ),
    wac: (
      <Grid key="wac">
        <Grid.Span columns={column21212}>
          <Shelf alignment="center" className={classes.fullHeight}>
            <Regular>WAC</Regular>
            <Shelf.Spacer />
          </Shelf>
        </Grid.Span>

        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__wac-brand-discount"
            name="wac_brand_discount"
            label="Brand Discount"
            suffix="%"
            instructions="No commas or special characters"
          />
        </Grid.Span>

        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__wac-generic-discount"
            name="wac_generic_discount"
            label="Generic Discount"
            suffix="%"
            instructions="No commas or special characters"
          />
        </Grid.Span>

        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__wac-brand-dispensing-fee"
            name="wac_brand_dispensing_fee"
            label="Brand Dispensing Fee"
            prefix="$"
            instructions="No commas or special characters"
          />
        </Grid.Span>

        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__wac-generic-dispensing-fee"
            name="wac_generic_dispensing_fee"
            label="Generic Dispensing Fee"
            prefix="$"
            instructions="No commas or special characters"
          />
        </Grid.Span>
      </Grid>
    ),
    usual_and_customary: (
      <Grid key="unc">
        <Grid.Span columns={column21212}>
          <Shelf alignment="center" className={classes.fullHeight}>
            <Regular>UNC</Regular>
            <Shelf.Spacer />
          </Shelf>
        </Grid.Span>
        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__awp-brand-dispensing-fee"
            name="awp_brand_dispensing_fee"
            label="Brand Dispensing Fee"
            prefix="$"
            instructions="No commas or special characters"
          />
        </Grid.Span>
        <Grid.Span columns={column266}>
          <Textbox.Field
            testId="test__awp-generic-dispensing-fee"
            name="awp_generic_dispensing_fee"
            label="Generic Dispensing Fee"
            prefix="$"
            instructions="No commas or special characters"
          />
        </Grid.Span>
      </Grid>
    ),
    submitted: (
      <Grid key="submitted">
        <Grid.Span columns={column21212}>
          <Shelf alignment="center" className={classes.fullHeight}>
            <Regular>Submitted Pricing</Regular>
            <Shelf.Spacer />
          </Shelf>
        </Grid.Span>
      </Grid>
    ),
  };

  /* Level Config */
  const spreadOverpaymentMethod = useWatch({ name: "spread_overpayment_method" });
  const overmaymentMethodInstructions =
    spreadOverpaymentMethod === "decrease_patient_pay"
    && "Choosing this option will prevent pricing levels from evaluating and overinflating pharmacy amounts.";

  /* Price Types */
  const selectedPriceType = useWatch({ name: "selected_price_types" });
  const priceTypeSelection = useWatch({ name: "price_type_selection" }) || "lesser_of";

  const clientId = useWatch({ name: "client_id" });

  /* Orderable Price Types */
  const [rows, setRows] = useState<ReadonlyArray<DataRow<RowData>>>([]);

  interface RowData {
    readonly name: string;
  }

  useEffect(() => {
    const items: ReadonlyArray<DataRow<RowData>> = (selectedPriceType || []).map((item, idx) => ({ id: idx, data: { name: item } }));
    setRows(items);
  }, [selectedPriceType]);

  const dataSource = useSimpleTableDataSource(rows);

  const rowChangeHandler = useCallback(async (rows: ReadonlyArray<DataRow<RowData>>) => {
    setRows(rows);

    /* update the selected_price_types form value each time their position is changed. */
    const updatedSelectedPriceTypes = rows?.map((row) => row.data.name);
    form.setValue("selected_price_types", updatedSelectedPriceTypes);
  }, []);

  const hasDrugListPermissions = useLoosePermissionCheck(["adjudication:drug_list_list"]);

  let drugListDataSource = null;

  if (hasDrugListPermissions) {
    drugListDataSource = useRemoteTableDataSource<DrugList.DrugListResult>(`${getApiBaseUrl()}/drug_list/list`, (row) => row.drug_list.id, {
      pageSize: 100,
      idFilterName: "ids",
    });
  }

  const clientIdRef = useRef(clientId);

  useEffect(() => {
    drugListDataSource.filterBy("client_ids", [clientId]);
    if (clientId) {
      if (clientIdRef.current != clientId) {
        form.setValue("drug_list_ids", []);
      }
    }
  }, [clientId]);

  const drugListLabels = useCallback((row) => {
    return `${row.drug_list.external_drug_list_id}`;
  }, []);

  return (
    <Panel>
      <Stack spacing="large">
        {/* Dates */}
        <Stack>
          <Heading>Dates</Heading>
          <Grid>
            <Grid.Spread columns={columns612}>
              <Datebox.Field
                testId="minimum_date_of_service"
                name="minimum_date_of_service"
                label="Minimum Date of Service"
                zone="utc"
                instructions="mm/dd/yyyy"
              />
              <Datebox.Field
                testId="maximum_date_of_service"
                name="maximum_date_of_service"
                label="Maximum Date of Service"
                zone="utc"
                instructions="mm/dd/yyyy"
              />
              <Datebox.Field
                testId="minimum_claim_submission_date"
                name="minimum_claim_submission_date"
                label="Minimum Claim Submission Date"
                zone="utc"
                instructions="mm/dd/yyyy"
              />
              <Datebox.Field
                testId="maximum_claim_submission_date"
                name="maximum_claim_submission_date"
                label="Maximum Claim Submission Date"
                zone="utc"
                instructions="mm/dd/yyyy"
              />
            </Grid.Spread>
          </Grid>
        </Stack>

        {/* Level Configuration */}
        <Stack>
          <Heading>Level Configuration</Heading>
          <Grid>
            <Grid.Spread columns={columns612}>
              <Dropdown.Field label="Level" name="level" testId="test__level" placeholder="Select" required>
                {renderLevelOptions}
              </Dropdown.Field>
              <Textbox.Field
                testId="test__ingredient-cost-percentage"
                name="ingredient_cost_percentage"
                label="Ingredient Cost Percentage"
                suffix="%"
                validator={numberValidator}
              />
              <Textbox.Field
                testId="test__dispensing-fee-percentage"
                name="dispensing_fee_percentage"
                label="Dispensing Fee Percentage"
                suffix="%"
                validator={numberValidator}
              />
              <Textbox.Field
                testId="test__ingredient-cost-amount"
                name="ingredient_cost_amount"
                label="Ingredient Cost Amount"
                prefix="$"
                validator={numberValidator}
                instructions="No commas or special characters"
              />
              <Textbox.Field
                testId="test__dispensing-fee-amount"
                name="dispensing_fee_amount"
                label="Dispensing Fee Amount"
                prefix="$"
                validator={numberValidator}
                instructions="No commas or special characters"
              />
              <Dropdown.Field
                label="Overpayment Method"
                name="spread_overpayment_method"
                testId="test__spread_overpayment_method"
                placeholder="Select"
                instructions={overmaymentMethodInstructions}
              >
                {renderOverpaymentMethodOptions}
              </Dropdown.Field>
              <Textbox.Field
                testId="test__price-list-ids"
                name="price_list_ids"
                label="Price List IDs"
                instructions="Separate multiple IDs by commas"
              />
            </Grid.Spread>
            <Grid.Break />
          </Grid>
        </Stack>

        {/* Hierarchy */}
        <PricingLevelFormHierarchy defaultValues={defaultValues} />

        {/* Pharmacy/Network */}
        <Stack>
          <Heading>Pharmacy/Network</Heading>
          <Grid>
            <Grid.Spread columns={columns612}>
              <Textbox.Field
                testId="test__provider-agreement-ids"
                name="provider_agreement_ids"
                label="Provider Agreement IDs"
                instructions="Separate multiple IDs by commas"
              />
              <Textbox.Field
                testId="test__service-provider-ids"
                name="service_provider_ids"
                label="Service Provider IDs (NPIs)"
                instructions="Separate multiple IDs by commas"
              />
              <Textbox.Field
                testId="test__relationship-ids"
                name="relationship_ids"
                label="Relationship IDs (Chain Codes)"
                instructions="Separate multiple IDs by commas"
              />
              <Textbox.Field
                testId="test__network-ids"
                name="network_ids"
                label="Internal Network IDs"
                instructions="Separate multiple IDs by commas"
              />
              <MultiSelectDataDropdown.Field
                dataSource={providerAgreementDataSourceDropdownData}
                optionLabelProvider={providerAgreementlabelProvider}
                name="external_provider_agreement_ids"
                searchField="external_provider_agreement_id"
                testId="test__external-provider-agreement-ids"
                label={"External Provider Agreement IDs"}
                placeholder="Select"
              />
              <MultiSelectDropdown.Field label="Agreement Types" name="agreement_types" placeholder="Select">
                {generateMultiSelectDropdownOptions(providerAgreementType)}
              </MultiSelectDropdown.Field>
              <MultiSelectDropdown.Field label="Agreement Channels" name="agreement_channels" placeholder="Select">
                {generateMultiSelectDropdownOptions(agreementChannel)}
              </MultiSelectDropdown.Field>
              <MultiSelectDropdown.Field label="Lines of Business" name="lines_of_business" placeholder="Select">
                {generateMultiSelectDropdownOptions(lineOfBusiness)}
              </MultiSelectDropdown.Field>
              <MultiSelectDataDropdown.Field
                dataSource={networkDataSourceDropdownData}
                optionLabelProvider={networklabelProvider}
                name="external_network_ids"
                searchField="external_network_id"
                testId="test__external-network-ids"
                label={"External Network IDs"}
                placeholder="Select"
              />
              <MultiSelectDropdown.Field label="Benefit Network Types" name="benefit_network_types" placeholder="Select">
                {generateMultiSelectDropdownOptions(networkBenefits)}
              </MultiSelectDropdown.Field>
            </Grid.Spread>
          </Grid>
        </Stack>

        {/* Claim */}
        <Stack>
          <Heading>Claim</Heading>
          <Grid>
            <Grid.Spread columns={columns612}>
              <Textbox.Field
                testId="test__minimum-quantity-dispensed"
                name="minimum_quantity_dispensed"
                label="Mininimum Quantity Dispensed"
                validator={numberValidator}
              />
              <Textbox.Field
                testId="test__maximum-quantity-dispensed"
                name="maximum_quantity_dispensed"
                label="Maximum Quantity Dispensed"
                validator={numberValidator}
              />
              <Textbox.Field
                testId="test__minimum-days-supply"
                name="minimum_days_supply"
                label="Mininimum Days Supply"
                validator={numberValidator}
              />
              <Textbox.Field
                testId="test__maximum-days-supply"
                name="maximum_days_supply"
                label="Maximum Days Supply"
                validator={numberValidator}
              />
            </Grid.Spread>
          </Grid>
        </Stack>

        {/* Drug */}
        <Stack>
          <Heading>Drug</Heading>
          <Grid>
            <Grid.Spread columns={columns612}>
              <Textbox.Field testId="test__ndc" name="ndc" label="NDC" />
              <Textbox.Field testId="test__gpi" name="gpi" label="GPI (Full or Partial)" />
              {hasDrugListPermissions && (
                <MultiSelectDataDropdown.Field
                  label="Drug List(s)"
                  name="drug_list_ids"
                  dataSource={drugListDataSource}
                  optionLabelProvider={drugListLabels}
                  searchField="external_drug_list_id"
                  disabled={!clientId}
                />
              )}
              <CheckboxGroup.Field orientation="horizontal" name="multi_source" label="Select MONY Code">
                <CheckboxGroup.Option value="M">M</CheckboxGroup.Option>
                <CheckboxGroup.Option value="O">O</CheckboxGroup.Option>
                <CheckboxGroup.Option value="N">N</CheckboxGroup.Option>
                <CheckboxGroup.Option value="Y">Y</CheckboxGroup.Option>
              </CheckboxGroup.Field>
            </Grid.Spread>
          </Grid>
        </Stack>

        <Stack>
          <RadioGroup.Field
            orientation="vertical"
            name="price_type_selection"
            label="Set Price Selection Based On"
            required
            defaultValue="lesser_of"
          >
            <RadioGroup.Option value="lesser_of">Lesser of JUDI selected options</RadioGroup.Option>
            <RadioGroup.Option value="first_of">
              First of selected JUDI options + compare to submitted pricing to find lesser of
            </RadioGroup.Option>
          </RadioGroup.Field>
        </Stack>

        <Stack>
          <Heading>Price Types</Heading>
          <CheckboxGroup.Field hideLabel name="selected_price_types" orientation="horizontal">
            <CheckboxGroup.Option value="awp">AWP</CheckboxGroup.Option>
            <CheckboxGroup.Option value="nadac">NADAC</CheckboxGroup.Option>
            <CheckboxGroup.Option value="wac">WAC</CheckboxGroup.Option>
            <CheckboxGroup.Option value="specialty_list">Specialty List</CheckboxGroup.Option>
            <CheckboxGroup.Option value="mac_list">MAC List</CheckboxGroup.Option>
            <CheckboxGroup.Option value="usual_and_customary">UNC</CheckboxGroup.Option>
            <CheckboxGroup.Option value="submitted">Submitted Pricing</CheckboxGroup.Option>
          </CheckboxGroup.Field>
        </Stack>

        {priceTypeSelection === "lesser_of" && (
          <Stack>{rows.map((row) => row.data.name !== "submitted" && fieldsObj[row.data.name])}</Stack>
        )}

        {priceTypeSelection === "first_of" && (
          <Stack>
            <OrderableList dataSource={dataSource} onChange={rowChangeHandler}>
              {(row) => fieldsObj[row.name]}
            </OrderableList>
          </Stack>
        )}
        <CompoundSection />
        {/* Notes */}
        <Stack>
          <Heading>Notes</Heading>
          <TextArea.Field hideLabel={true} lines={4} name="notes" testId="test__notes" />
        </Stack>
      </Stack>
    </Panel>
  );
};
